var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Point } from '@pacem/pacem-foundation';
import { Utils, PCSS, Transformer } from '@pacem/pacem-core';
import { Components as ComponentsDrawing, Drawing } from '@pacem/pacem-2d';
const PALETTE = [() => Utils.Css.getVariableValue('--color-default'),
    '#999', '#808000', '#80006e', '#d69d73', '#308978', '#429cd6'];
const balloons = new WeakMap();
function isBorderSegment(item) {
    return Drawing.isShape(item) && 'data' in item && Utils.isArray(item['data']);
}
function isNeuron(item) {
    return Drawing.isShape(item) && 'parent' in item && Utils.isArray(item['parent']['items']);
}
function sliceClassColor(cls) {
    var clr = PALETTE[cls.index % PALETTE.length];
    if (typeof clr === 'function') {
        clr = clr();
    }
    return clr;
}
function showSplitHint(item, setup) {
    const p1 = item.data[0], p2 = item.data[1];
    if (setup) {
        item.stroke = Utils.Css.getVariableValue('--color-accent');
        const midpoint = {
            x: .5 * (p1.x + p2.x), y: .5 * (p1.y + p2.y)
        }, circleShadowData = ComponentsDrawing.Drawing.PacemCircleElement.getPathData(midpoint, .25);
        item.pathData = `M${p1.x},${p1.y} L${p2.x},${p2.y} ${circleShadowData}`;
    }
    else {
        item.stroke = sliceClassColor(item.class);
        item.pathData = `M${p1.x},${p1.y} L${p2.x},${p2.y}`;
    }
    item.stage.draw(item);
}
function neuronTooltip(balloon, coords) {
    if (balloons.has(balloon)) {
        const handle = balloons.get(balloon);
        window.clearTimeout(handle);
    }
    if (coords) {
        balloon.target = coords;
        balloon.popup();
    }
    else {
        const handle = window.setTimeout(() => balloon.popout(), 500);
        balloons.set(balloon, handle);
    }
}
class DemoTransformers {
    static drawifyBrainSlice(slice, stage) {
        const drawables = [];
        let j = 0;
        for (let cls of slice.classes || []) {
            const isBorder = cls.index === 0;
            const drawable = { stage: stage, items: [] };
            var color = PALETTE[j % PALETTE.length];
            if (typeof color === 'function') {
                color = color();
            }
            let k = 0;
            for (let vertex of cls.vertices || []) {
                if (isBorder && cls.vertices.length > k + 1) {
                    const p1 = vertex, p2 = cls.vertices[k + 1];
                    const segment = {
                        fill: 'none', stroke: color, stage: stage,
                        lineWidth: .05, opacity: .333,
                        pathData: `M${p1.x},${p1.y} L${p2.x},${p2.y}`,
                        data: [p1, p2],
                        index: k, parent: drawable, class: cls
                    };
                    drawable.items.push(segment);
                }
                const path = {
                    fill: color,
                    lineWidth: 0, opacity: .5, pathData: ComponentsDrawing.Drawing.PacemCircleElement.getPathData({ x: vertex.x, y: vertex.y }, isBorder ? .25 : .1),
                    stage: stage, draggable: isBorder,
                    data: vertex,
                    index: drawable.items.length, parent: drawable, center: vertex
                };
                drawable.items.push(path);
                k++;
            }
            drawables.push(drawable);
            j++;
        }
        return drawables;
    }
    static trayBrainBorder(neuron, offset, establish) {
        const ndx = neuron.index;
        const border = neuron.parent;
        const stage = neuron.stage;
        const center = Point.add(offset, neuron.center);
        if (ndx >= 3) {
            const lhs = border.items[ndx - 3];
            if (Drawing.isShape(lhs)) {
                const lPathData = lhs.pathData.split(' ');
                lhs.pathData = lPathData[0] + ` L${center.x},${center.y}`;
                stage.draw(lhs);
            }
        }
        if (establish) {
            neuron.pathData = ComponentsDrawing.Drawing.PacemCircleElement.getPathData(center, .25);
            neuron.center = center;
            stage.draw(neuron);
        }
        if (ndx >= 1) {
            const rhs = border.items[ndx - 1];
            if (Drawing.isShape(rhs)) {
                const rPathData = rhs.pathData.split(' ');
                rhs.pathData = `M${center.x},${center.y} ` + rPathData[1];
                stage.draw(rhs);
            }
        }
    }
    static handleItemOver(evt, balloon) {
        if (isBorderSegment(evt.detail)) {
            if (evt.ctrlKey) {
                showSplitHint(evt.detail, true);
            }
            else {
                showSplitHint(evt.detail, false);
            }
        }
        else if (isNeuron(evt.detail)) {
            if (evt.shiftKey) {
                neuronTooltip(balloon, { x: evt.pageX, y: evt.pageY });
            }
            else {
                neuronTooltip(balloon, false);
            }
        }
    }
    static handleItemOut(evt, balloon) {
        if (isBorderSegment(evt.detail)) {
            showSplitHint(evt.detail, false);
        }
        else if (isNeuron(evt.detail)) {
            neuronTooltip(balloon, false);
        }
    }
    static handleItemClick(evt, repo) {
        if (isBorderSegment(evt.detail) && evt.ctrlKey) {
            const slice = repo.model, border = evt.detail, cls = border.class, clsIndex = slice.classes.indexOf(cls);
            if (clsIndex === -1) {
                throw 'WTF!?...';
            }
            const p1 = border.data[0], p2 = border.data[1], index = border.index + 1;
            cls.vertices.splice(index, 0, { timestamp: new Date(), x: .5 * (p1.x + p2.x), y: .5 * (p1.y + p2.y) });
            repo.model = Utils.clone(repo.model);
        }
    }
    static async markerIcon() {
        const fillColor = Utils.Css.getVariableValue(`--${PCSS}-color-accent`);
        const dur = 1.5;
        const marker = `<svg xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 0 24 24" width="48">
    <circle cx="12" cy="12" r="12" fill="${fillColor}">
        <animate attributeName="opacity" values="0.1;0.5;0" dur="${dur}s" repeatCount="indefinite" />
        <animate attributeName="r" values="5;12" dur="${dur}s" repeatCount="indefinite"
                 calcMode="spline"
                 keySplines="0.47 0 0.745 0.715" />
    </circle>
    <circle cx="12" cy="12" r="5" fill="#fff" />
    <circle cx="12" cy="12" r="2" fill="#000" />
</svg>`;
        const blob = Utils.textToBlob(marker, "image/svg+xml");
        const url = await Utils.blobToDataURL(blob);
        return {
            url, size: { width: 48, height: 48 }, anchor: { x: 24, y: 24 }, html: false
        };
    }
}
__decorate([
    Transformer()
], DemoTransformers, "drawifyBrainSlice", null);
__decorate([
    Transformer()
], DemoTransformers, "trayBrainBorder", null);
__decorate([
    Transformer()
], DemoTransformers, "handleItemOver", null);
__decorate([
    Transformer()
], DemoTransformers, "handleItemOut", null);
__decorate([
    Transformer()
], DemoTransformers, "handleItemClick", null);
__decorate([
    Transformer('demo.')
], DemoTransformers, "markerIcon", null);
